/** What qualifies a filing's total: the notices that sit beside the disclosures (moved out of
 * `Reports.tsx` unchanged; the comments are the original ones).
 *
 * **Beside the figure, not in a footnote.** A reader who has reached the citations has already
 * believed the total, so each of these says what the total is not (a partial period, an unevidenced
 * share, a company left out, a base year nobody has decided) where it is read. Warm only where a
 * person has to do something; a correctly disclosed exclusion is a note, not an alarm.
 */

import type { Report } from "../api/queries";
import { shortDate } from "../lib/format";
import { FiguresInRegister } from "./ReportsLines";
import own from "./Reports.module.css";
import { amount, cls } from "./reportsFormat";


export function Qualifiers({ report, role }: { report: Report; role: string | undefined }) {
  const unconsolidatedCompanyCount = new Set(
    report.context.unconsolidated.map((member) => member.legal_entity_id),
  ).size;
  return (
    <>
      {/* **Notices that qualify the total.** Warm (`noticeWarn`) only where somebody has to do
          something; a correctly disclosed exclusion is a note, not an alarm. */}
      {report.context.clamped_by_engagement ? (
        <p className={cls(own.notice, own.noticeWarn)}>
          <span className={cls(own.noticeTitle)}>This is a partial period</span>
          Your engagement covers less than this filing&rsquo;s financial year, so the figures
          in the disclosures are for the part you are engaged for. They are not the company&rsquo;s
          totals.
        </p>
      ) : null}

      {/* A figure with **no** evidence at all is a broken chain, not a grade - so it is said
          separately and in stronger terms. Folding it into the sentence below would hide it
          inside a data-quality statement, which is the shape of F10. */}
      {Number(report.unevidenced_tonnes) > 0 ? (
        <p className={cls(own.notice, own.noticeWarn)} data-testid="unevidenced">
          <span className={cls(own.noticeTitle)}>Some of this total has no evidence at all</span>
          {amount(report.unevidenced_tonnes, "tCO2e")} tCO₂e cites neither a document nor a
          stated basis. That is not a weaker kind of evidence, it is none: open the figures in
          the register and either attach what they rest on or record what they were based on.{" "}
          <FiguresInRegister report={report} status="broken" testId="report-unevidenced-link">
            Show the figures whose evidence chain is incomplete
          </FiguresInRegister>
        </p>
      ) : null}

      {/* **CTX-02: the group changed shape, and EmitIQ says it cannot tell what that means.**
       *
       * Present only when a company joined or left the group, or one of this entity's own sites
       * opened or closed, inside the period. It states the changes and the days, then the
       * server's one sentence about what is not known - **M-03**: the verification log has no
       * entry on base-year recalculation, so there is no threshold to compare against and
       * nothing here is a verdict. No count, no percentage and no "required" anywhere; a field
       * for one is the change the backend's tests exist to refuse.
       *
       * `?.` guards the context and the truthiness test guards the notice, so a response (or a
       * hand-written stub) without the key renders nothing rather than crashing the panel. The
       * sentences are the API's and are not reworded here: written once, so two screens cannot
       * describe the same change differently. */}
      {role === "auditor" ? (
        <p className={cls(own.notice)} data-testid="group-visibility-scope">
          Group companies and changes shown here are limited to your engagement. A company or
          change missing from this view may be outside your access.
        </p>
      ) : null}

      {report.context.base_year_notice ? (
        <p className={cls(own.notice, own.noticeWarn)} data-testid="base-year-notice">
          <span className={cls(own.noticeTitle)}>
            Base year: this needs a person&rsquo;s decision
          </span>
          {report.context.base_year_notice.changes.map((change) => (
            <span key={`${change.kind}-${change.subject_id}-${change.on}`} className={cls(own.noticeLine)}>
              {change.sentence}
            </span>
          ))}
          {report.context.base_year_notice.sentence}
        </p>
      ) : null}

      {/* **A company in the group that this report does not cover** (ADR-0031).
       *
       * The product knows which companies were in the group and for which days; consolidating
       * them needs the boundary approach to decide whole, equity share, or not at all, and that
       * decision is not made (S09). A reader who knows a subsidiary exists and sees no sign of
       * it in the figures would otherwise have to guess between excluded, forgotten and nil.
       *
       * Beside the verdict rather than in a footnote, because it qualifies the total: a group
       * reading its parent's SECR return needs to know the number is not the group's. */}
      {report.context.unconsolidated.length ? (
        <p className={cls(own.notice)} data-testid="unconsolidated">
          <span className={cls(own.noticeTitle)}>
            {unconsolidatedCompanyCount === 1
              ? "One company visible in the group is not in this report"
              : `${unconsolidatedCompanyCount} companies visible in the group are not in this report`}
          </span>
          {report.context.unconsolidated.map((member) => (
            <span key={`${member.legal_entity_id}-${member.from_date}-${member.to_date}-${member.included}`} className={cls(own.noticeLine)}>
              <strong>{member.legal_entity_name}</strong>
              {` was in the group from ${shortDate(member.from_date)} through ${shortDate(member.to_date)}`}
              {member.included ? "" : "; its ownership relationship is marked outside the reporting boundary"}.
            </span>
          ))}
          These figures are {report.context.legal_entity_name}&rsquo;s own sites. Consolidating a
          subsidiary depends on the boundary approach (whether it is included in full, at an
          equity share, or not at all) and EmitIQ does not make that choice for you yet. Each
          company that files its own return has its own filing obligation above.
        </p>
      ) : null}

      {/* **How much of this total rests on somebody's word** (ADR-0036 decision 3).
       *
       * A company filing a number that is 40% attested should hear it from us before an
       * assurance provider says it. It is also what makes the decision reversible: if the rule
       * is ever tightened, the affected figures are already identified.
       *
       * **Marked as ours, not the regulation's.** The verification log carries nothing on an
       * obligation to disclose data quality under either regime (rule 2), so this sits apart
       * from the lines, has no paragraph beside it, and says whose statement it is. */}
      {report.data_quality_note ? (
        <p className={cls(own.notice)} data-testid="data-quality">
          <span className={cls(own.noticeTitle)}>
            Data quality: EmitIQ&rsquo;s note, not a required disclosure
          </span>
          {report.data_quality_note}
        </p>
      ) : null}
    </>
  );
}
